import { motion } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import {
  CloudIcon,
  SunIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  BoltIcon,
  MoonIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  getCurrentWeather,
  getWeatherForecast,
  getWeatherForDate,
  getClothingRecommendations,
  formatTemperature,
  getWeatherIcon,
} from '@/lib/weather';

export default function WeatherWidget({ event, className = '' }) {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [shouldShow, setShouldShow] = useState(true);
  const [isToday, setIsToday] = useState(false);

  const { latitude, longitude, datestart, timestart } = event;

  const hasLocation = latitude && longitude;
  const eventDate = datestart ? new Date(datestart) : null;

  // Check if event is today - done in useEffect to prevent hydration mismatch
  useEffect(() => {
    if (eventDate) {
      setIsToday(eventDate.toDateString() === new Date().toDateString());
    }
  }, [datestart]);

  const fetchWeatherData = useCallback(async () => {
    if (!hasLocation) return;

    setLoading(true);
    setError(null);

    try {
      const [current, forecast] = await Promise.all([
        getCurrentWeather(latitude, longitude),
        getWeatherForecast(latitude, longitude),
      ]);

      setWeatherData({ current, forecast });
      setLastUpdated(new Date());
      setShouldShow(true);
    } catch (err) {
      setError(err.message);

      // Don't show the widget if there's an API error
      if (err.message.includes('401') || err.message.includes('API key')) {
        setShouldShow(false);
      }
    } finally {
      setLoading(false);
    }
  }, [latitude, longitude, hasLocation]);

  // Fetch weather data when component mounts or location changes
  useEffect(() => {
    if (hasLocation) {
      fetchWeatherData();
    }
  }, [fetchWeatherData, hasLocation]);

  const handleRefresh = () => {
    fetchWeatherData();
  };

  // Get event-specific weather data
  const eventWeather =
    eventDate && weatherData?.forecast
      ? getWeatherForDate(weatherData.forecast, eventDate)
      : null;

  // Get clothing recommendations
  const clothingRecommendations = eventWeather
    ? getClothingRecommendations(
        eventWeather.main?.temp,
        eventWeather.weather?.[0]?.main
      )
    : [];

  // Get weather icon component
  const getWeatherIconComponent = (iconCode) => {
    const iconName = getWeatherIcon(iconCode);
    const iconMap = {
      SunIcon: SunIcon,
      MoonIcon: MoonIcon,
      CloudIcon: CloudIcon,
      BoltIcon: BoltIcon,
    };
    return iconMap[iconName] || CloudIcon;
  };

  // Safety check for event data
  if (!event) {
    return null;
  }

  // Don't render if no location or if we've decided not to show due to API errors
  if (!hasLocation || !shouldShow) {
    return null;
  }

  // Don't render while loading (to avoid flash of content)
  if (loading) {
    return null;
  }

  // Don't render if there's an error
  if (error) {
    return null;
  }

  // Don't render if no weather data
  if (!weatherData) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.4 }}
      className={`bg-white rounded p-6 ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-brown-200">Weather</h3>
        </div>
        {/* {hasLocation && (
                    <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="p-1 text-green-100 hover:text-green-100/80 transition-colors disabled:opacity-50"
                        aria-label="Refresh weather data"
                    >
                        <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                )} */}
      </div>

      {!hasLocation ? (
        <div className="bg-beige-50 rounded-lg border border-beige-100">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-coral-100" />
            <p className="text-sm font-medium text-brown-200">
              Location Required
            </p>
          </div>
          <p className="text-xs text-brown-100">
            Weather information requires event location coordinates. Check the
            event details for location information.
          </p>
        </div>
      ) : loading ? (
        <div className="bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex items-center gap-3">
            <ArrowPathIcon className="w-6 h-6 text-blue-100 animate-spin" />
            <p className="text-sm text-blue-100">Loading weather data...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 rounded-lg border border-red-100">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-100" />
            <p className="text-sm font-medium text-brown-200">Weather Error</p>
          </div>
          <p className="text-xs text-red-100 mb-2">{error}</p>
          <button
            onClick={handleRefresh}
            className="text-xs text-red-100 hover:text-red-100/80 underline"
          >
            Try again
          </button>
        </div>
      ) : weatherData ? (
        <div className="space-y-4">
          {/* Current Weather */}
          <div className="">
            <div className="flex items-center gap-3 mb-2 w-fit mx-auto mb-4 rounded bg-beige-30/50 px-5 py-4">
              {(() => {
                const IconComponent = getWeatherIconComponent(
                  weatherData.current.weather[0]?.icon
                );
                return <IconComponent className="w-6 h-6" />;
              })()}
              <div>
                <p className="text-sm font-bold">Current Conditions</p>
                <p className="text-xs text-brown-100">
                  {weatherData.current.weather[0]?.description || 'Unknown'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-center text-sm">
              <div>
                <p className="font-black">Temperature</p>
                <p className="text-brown-100">
                  {formatTemperature(weatherData.current.main?.temp)}
                </p>
              </div>
              <div>
                <p className="font-black">Feels Like</p>
                <p className="text-brown-100">
                  {formatTemperature(weatherData.current.main?.feels_like)}
                </p>
              </div>
              <div>
                <p className="font-black">Humidity</p>
                <p className="text-brown-100">
                  {weatherData.current.main?.humidity || 'N/A'}%
                </p>
              </div>
              <div>
                <p className="font-black">Wind</p>
                <p className="text-brown-100">
                  {Math.round(weatherData.current.wind?.speed || 0)} mph
                </p>
              </div>
            </div>
          </div>

          {/* Event Date Weather */}
          {eventWeather && (
            <div className="p-4 bg-beige-50 rounded-lg border border-beige-100">
              <div className="flex items-center gap-3 mb-2">
                {(() => {
                  const IconComponent = getWeatherIconComponent(
                    eventWeather.weather[0]?.icon
                  );
                  return <IconComponent className="w-6 h-6 text-brown-100" />;
                })()}
                <div>
                  <p className="text-sm font-medium text-brown-200">
                    {isToday ? 'Today' : eventDate.toLocaleDateString()}
                  </p>
                  <p className="text-xs text-brown-100">
                    {timestart && `Event time: ${timestart}`}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-brown-200">High</p>
                  <p className="text-brown-100">
                    {formatTemperature(eventWeather.main?.temp_max)}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-brown-200">Low</p>
                  <p className="text-brown-100">
                    {formatTemperature(eventWeather.main?.temp_min)}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-brown-200">Conditions</p>
                  <p className="text-brown-100">
                    {eventWeather.weather[0]?.description || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-brown-200">Precipitation</p>
                  <p className="text-brown-100">
                    {Math.round((eventWeather.pop || 0) * 100)}%
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Clothing Recommendations */}
          {clothingRecommendations.length > 0 && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-start gap-2">
                <InformationCircleIcon className="w-5 h-5 text-green-100 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-brown-200 mb-2">
                    What to Wear
                  </p>
                  <ul className="space-y-1">
                    {clothingRecommendations.slice(0, 4).map((item, index) => (
                      <li
                        key={index}
                        className="text-xs text-brown-100 flex items-center gap-2"
                      >
                        <span className="w-1.5 h-1.5 bg-green-100 rounded-full"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Last Updated */}
          {lastUpdated && (
            <div className="text-center">
              <p className="text-xxs text-brown-100">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            </div>
          )}
        </div>
      ) : null}
    </motion.div>
  );
}
